import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Link as MuiLink,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

import {
  listMyBillingProfiles,
  listMyThresholdCharges,
} from '@src/api/myBilling';
import { useAuth } from '@src/context';

const OUTCOME_CHIP_COLORS = {
  pending: 'default',
  charged: 'success',
  retried_success: 'success',
  failed: 'error',
  paused: 'error',
};

const STATUS_LABEL = {
  pending: 'Pending',
  charged: 'Successful',
  retried_success: 'Successful (retry)',
  failed: 'Failed',
  paused: 'Failed',
};

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return value;
  }
};

export default function MyBillingActivityPage() {
  const { state: authState } = useAuth();
  const isStaff =
    authState?.user?.is_staff || authState?.user?.is_superuser;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // null = still resolving the visibility flag; gate render until known so
  // a non-cohort client never flashes the experimental UI.
  const [uiEnabled, setUiEnabled] = useState(null);

  useEffect(() => {
    let alive = true;
    listMyBillingProfiles()
      .then((resp) => {
        if (!alive) return;
        const data = resp?.data;
        const rows = Array.isArray(data) ? data : data?.results || [];
        setUiEnabled(rows.some((p) => p.client_billing_ui_enabled));
      })
      .catch(() => {
        if (alive) setUiEnabled(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const params = useMemo(() => {
    const p = {};
    if (statusFilter) p.outcome = statusFilter;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  }, [statusFilter, dateFrom, dateTo]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await listMyThresholdCharges(params);
      const data = resp?.data;
      setRows(Array.isArray(data) ? data : data?.results || []);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load.');
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  // Filter rows that actually represent client-visible charges (pending,
  // charged, retried_success, failed). Skip internal-only entries.
  const visibleRows = useMemo(
    () =>
      rows.filter((r) =>
        ['pending', 'charged', 'retried_success', 'failed', 'paused'].includes(
          r.outcome,
        ),
      ),
    [rows],
  );

  const total = useMemo(() => {
    return visibleRows
      .filter((r) => r.outcome === 'charged' || r.outcome === 'retried_success')
      .reduce((sum, r) => sum + Number(r.cumulative_spend || 0), 0);
  }, [visibleRows]);

  // Staff always see the activity for every client regardless of the
  // flag — the gate is purely a CLIENT-facing rollout control.
  if (!isStaff && uiEnabled === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isStaff && !uiEnabled) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">
          Your billing is managed directly by your Lead Gurus account
          manager. Please reach out to them with any billing questions.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5">Payment Activity</Typography>
          <Typography variant="body2" color="text.secondary">
            Your full billing history
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={load}>
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={4}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Charges
              </Typography>
              <Typography variant="h6">
                {visibleRows.filter(
                  (r) =>
                    r.outcome === 'charged' || r.outcome === 'retried_success',
                ).length}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Total charged
              </Typography>
              <Typography variant="h6">{formatCurrency(total)}</Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <TextField
              select
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="charged">Successful</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </TextField>
            <TextField
              size="small"
              type="date"
              label="From"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              type="date"
              label="To"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              size="small"
              onClick={() => {
                setStatusFilter('');
                setDateFrom('');
                setDateTo('');
              }}
            >
              Clear
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : visibleRows.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No charges yet.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Receipt</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{formatDate(row.triggered_at)}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(row.cumulative_spend)}
                      </TableCell>
                      <TableCell>
                        {row.invoice_payment_method || '—'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={STATUS_LABEL[row.outcome] || row.outcome}
                          color={OUTCOME_CHIP_COLORS[row.outcome] || 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {row.stripe_receipt_url ? (
                          <MuiLink
                            href={row.stripe_receipt_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Receipt
                          </MuiLink>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}
